<script setup>
import Modal from "@/components/modal/Modal";
import * as yup from "yup";
import {useDjangoForm} from "@/utils/forms";
import {useMutation} from "@vue/apollo-composable";
import {CreateHpoMutation} from "@/graphql/mutations/hpo/createHpoMutation.graphql";
import HpoListQuery from "@/graphql/queries/hpo/hpoList.graphql"
import {numberValidator, requiredMessage, stringValidator} from "../../utils/yupValidators";
import HpoMapModal from "@/components/hpo/HpoMapModal.vue";
import {ref} from "vue";

const emit = defineEmits(['close']);
const showMapModal = ref(false);

function close() {
  emit('close')
}

const schema = yup.object({
  name: stringValidator.required(requiredMessage),
  incidentNumber: yup.number()
    .required(requiredMessage)
    .min(1, gettext("Value can't be less than 1"))
    .max(32767, gettext("Value can't be more than 32767")),
  longitude: numberValidator,
  latitude: numberValidator,
})

const {mutate} = useMutation(CreateHpoMutation, {
  update: (cache, {data: {createHpoMutation}}) => {
    if (createHpoMutation.errors.length === 0) {
      let data = cache.readQuery({query: HpoListQuery})
      let updatedHpoList = [...data.hpoList]
      updatedHpoList.unshift(createHpoMutation.hpo)
      data = {...data, hpoList: updatedHpoList}
      cache.writeQuery({query: HpoListQuery, data});
    }
  }
});

const {defineField, handleMutationSubmit, isSubmitting} = useDjangoForm({
  validationSchema: schema,
  initialValues: {
    name: '',
    incidentNumber: null,
    longitude: null,
    latitude: null,
  }
});

const [name, nameProps] = defineField('name');
const [incidentNumber, incidentNumberProps] = defineField('incidentNumber');
const [latitude, latitudeProps] = defineField('latitude');
const [longitude, longitudeProps] = defineField('longitude');

const onSubmit = handleMutationSubmit(mutate, {
  buildValues(values) {
    if (!values.incidentNumber) {
      values.incidentNumber = 999
    }
  },
  onSuccess() {
    close();
  },
});
</script>


<template>
  <Modal :max-width="600"
         :title="pgettext('label', 'Create new MCI')"
         @close="close"
  >
    <v-form @submit.prevent="onSubmit">
      <v-row>
        <v-col cols="12">
          <!-- MCI Name -->
          <v-text-field v-model="name"
                        v-bind="nameProps"
                        autocomplete="off"
                        :placeholder="gettext('E.g. name of closest town')"
                        :label="pgettext('label', 'Incident Name')"
          ></v-text-field>
          <!-- Incident Number -->
          <v-number-input v-model="incidentNumber" v-bind="incidentNumberProps"
                          :label="pgettext('label', 'Incident number')"
                          :min="1" :step="1"
                          control-variant="hidden"/>

          <!-- GPS coordinates --->
          <div class="d-flex ga-6 align-center mb-6" style="max-width: 640px;">
            <v-btn type="button" @click="showMapModal = true">
              {{ pgettext('action', 'Select GPS Position') }}
            </v-btn>
            <v-text-field v-model="latitude"
                          v-bind="latitudeProps"
                          :label="pgettext('label', 'Latitude')"
                          variant="outlined"
                          hide-details
                          readonly/>
            <v-text-field v-model="longitude"
                          v-bind="longitudeProps"
                          :label="pgettext('label', 'Longitude')"
                          variant="outlined"
                          hide-details
                          readonly/>
          </div>

          <v-btn type="submit" block :loading="isSubmitting">
            {{ pgettext('action', 'Submit') }}
          </v-btn>
        </v-col>
      </v-row>
    </v-form>

    <HpoMapModal v-model="showMapModal"
                 :latitude="latitude"
                 :longitude="longitude"
                 @select="values => {latitude = values.latitude; longitude = values.longitude}"/>
  </Modal>
</template>